<template>
  <div class="system-page header-space">
    <div class="system-page__inner">
      <header class="page-header">
        <div class="page-header__text">
          <p class="page-kicker">{{ t('kicker') }}</p>
          <h1>{{ t('title') }}</h1>
          <p class="page-lead">{{ t('intro') }}</p>
        </div>
        <div class="language-selector" role="group" :aria-label="t('languageAria')">
          <button
            type="button"
            class="lang-btn"
            :class="{ 'lang-btn--active': lang === 'en' }"
            :aria-pressed="lang === 'en' ? 'true' : 'false'"
            :aria-label="t('switchToEn')"
            @click="setLang('en')"
          >
            EN
          </button>
          <button
            type="button"
            class="lang-btn"
            :class="{ 'lang-btn--active': lang === 'de' }"
            :aria-pressed="lang === 'de' ? 'true' : 'false'"
            :aria-label="t('switchToDe')"
            @click="setLang('de')"
          >
            DE
          </button>
        </div>
      </header>

      <section class="section-card">
        <h2 class="section-card__title">{{ t('dumpsTitle') }}</h2>
        <p class="section-card__lead">{{ t('dumpsIntro') }}</p>

        <div class="dump-block">
          <div class="dump-block__head">
            <h3 class="dump-block__title">{{ t('dumpQuickTitle') }}</h3>
            <p class="dump-block__hint">{{ t('dumpQuickHint') }}</p>
          </div>
          <div class="action-row">
            <button
              type="button"
              class="admin-btn admin-btn--primary"
              :disabled="!!dumpLoading"
              @click="createFullDump"
            >
              {{ dumpLoading === 'full' ? t('pleaseWait') : t('dumpCreate') }}
            </button>
            <button
              type="button"
              class="admin-btn admin-btn--ghost"
              :disabled="!!dumpLoading"
              @click="createSlimDump"
            >
              {{ dumpLoading === 'slim' ? t('pleaseWait') : t('dumpCreateSlim') }}
            </button>
          </div>
        </div>

        <div class="dump-block dump-block--custom">
          <div class="dump-block__head">
            <h3 class="dump-block__title">{{ t('dumpCustomTitle') }}</h3>
            <p class="dump-block__hint">{{ t('dumpTablesHint') }}</p>
          </div>

          <div class="sys-filter-field">
            <span class="sys-filter-field__label" id="dump-tables-label">{{ t('dumpTablesLabel') }}</span>
            <div class="sys-filter-field__control sys-filter-field__control--multi">
              <v-select
                v-model="selectedTables"
                :items="tables"
                :aria-labelledby="'dump-tables-label'"
                multiple
                chips
                deletable-chips
                dense
                solo
                flat
                hide-details
                :menu-props="tablesMenuProps"
              />
            </div>
          </div>

          <div class="action-row">
            <button
              type="button"
              class="admin-btn admin-btn--primary"
              :disabled="!!dumpLoading || selectedTables.length === 0"
              @click="createCustomDump"
            >
              {{ dumpLoading === 'custom' ? t('pleaseWait') : t('dumpCreateCustom') }}
            </button>
            <span v-if="selectedTables.length" class="dump-selection-count">
              {{ t('dumpSelectedCount').replace('{n}', String(selectedTables.length)) }}
            </span>
          </div>
        </div>

        <div v-show="initiated" class="data-panel">
          <div class="data-panel__head">
            <h3>{{ t('dumpsHistoryTitle') }}</h3>
          </div>
          <div class="data-panel__body">
            <div v-if="!loading && dumpList.length === 0" class="data-panel__empty-state">
              <p class="data-panel__empty-title">{{ t('dumpsEmptyTitle') }}</p>
              <p class="data-panel__empty">{{ t('dumpsEmpty') }}</p>
            </div>
            <table v-else-if="!loading" class="system-table">
              <thead>
                <tr>
                  <th class="col-name">{{ t('dumpsColName') }}</th>
                  <th>{{ t('dumpsColSize') }}</th>
                  <th>{{ t('dumpsColDate') }}</th>
                  <th>{{ t('dumpsColDownload') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in dumpList" :key="item.file">
                  <td class="col-name">{{ item.file }}</td>
                  <td>{{ item.size }}</td>
                  <td class="col-date">{{ item.createdAt || '—' }}</td>
                  <td>
                    <button
                      type="button"
                      class="sys-link-btn"
                      :disabled="downloadLoading === item.file"
                      @click="dbDumpDownload(item.file)"
                    >
                      {{ downloadLoading === item.file ? t('pleaseWait') : t('download') }}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section v-if="$auth.isAdmin()" class="section-card">
        <h2 class="section-card__title">{{ t('schemaTitle') }}</h2>
        <p class="section-card__lead">{{ t('schemaIntro') }}</p>
        <ol class="schema-steps">
          <li>{{ t('schemaStep1') }}</li>
          <li>{{ t('schemaStep2') }}</li>
          <li>{{ t('schemaStep3') }}</li>
        </ol>
        <p class="hint-text hint-text--lead">{{ t('schemaStepHint') }}</p>

        <aside class="callout callout--info" aria-label="lock_keys">
          <div class="callout__icon" aria-hidden="true">
            <v-icon small>mdi-information-outline</v-icon>
          </div>
          <div class="callout__body">
            <p class="callout__title">{{ t('schemaLockKeysTitle') }}</p>
            <p class="callout__text">{{ t('schemaLockKeysBody') }}</p>
          </div>
        </aside>

        <div class="schema-toolbar">
          <div class="action-row">
            <button
              type="button"
              class="admin-btn admin-btn--primary"
              :disabled="!!schemaLoading"
              @click="schemaPreview"
            >
              {{ schemaLoading === 'preview' ? t('pleaseWait') : t('schemaPreview') }}
            </button>
            <button
              type="button"
              class="admin-btn admin-btn--ghost"
              :disabled="!!schemaLoading"
              @click="schemaValidate"
            >
              {{ schemaLoading === 'validate' ? t('pleaseWait') : t('schemaValidate') }}
            </button>
            <button
              type="button"
              class="admin-btn admin-btn--danger"
              :disabled="!!schemaLoading || !schemaPreviewDone"
              @click="schemaApplyConfirm = true"
            >
              {{ schemaLoading === 'apply' ? t('pleaseWait') : t('schemaApply') }}
            </button>
          </div>

          <p
            class="status-chip"
            :class="schemaPreviewDone ? 'status-chip--ready' : 'status-chip--locked'"
          >
            <span class="status-chip__dot" aria-hidden="true" />
            {{ schemaPreviewDone ? t('schemaReady') : t('schemaApplyLocked') }}
          </p>
        </div>

        <aside v-if="schemaError" class="callout callout--error" role="alert">
          <div class="callout__body">
            <p class="callout__text">{{ schemaError }}</p>
          </div>
        </aside>

        <div class="data-panel data-panel--schema">
          <div class="data-panel__head">
            <h3>{{ schemaOutputTitle }}</h3>
          </div>
          <pre
            class="schema-output"
            :class="{ 'schema-output--ok': schemaOutputLooksOk }"
          >{{ schemaOutputDisplay }}</pre>
        </div>
      </section>

      <section v-show="initiated" class="section-card">
        <h2 class="section-card__title">{{ t('infoTitle') }}</h2>
        <p class="section-card__lead">{{ t('infoIntro') }}</p>

        <div class="data-panel">
          <div class="data-panel__body">
            <table v-if="!loading" class="system-table">
              <thead>
                <tr>
                  <th class="col-name">{{ t('infoColName') }}</th>
                  <th>{{ t('infoColValue') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="metric in systemInformation" :key="metric.name">
                  <td>{{ metric.name }}</td>
                  <td v-if="Array.isArray(metric.value)">
                    <code>
                      <span v-for="(metricLine, lineIndex) in metric.value" :key="lineIndex">
                        {{ metricLine }}<br>
                      </span>
                    </code>
                  </td>
                  <td v-else>
                    {{ metric.value }}
                  </td>
                </tr>
                <tr>
                  <td>{{ t('vueTimeout') }}</td>
                  <td>{{ $http.defaults.timeout / 1000 }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <AdminConfirmDialog
        v-model="schemaApplyConfirm"
        :title="t('schemaConfirmTitle')"
        :body="t('schemaConfirmBody')"
        :cancel-label="t('cancel')"
        :confirm-label="t('apply')"
        :busy-label="t('pleaseWait')"
        :busy="schemaLoading === 'apply'"
        confirm-variant="danger"
        @confirm="schemaApply"
      />
    </div>
  </div>
</template>

<script>
import AdminConfirmDialog from '@/components/AdminConfirmDialog.vue'
import { systemT } from '@/i18n/system'
import { readStoredLang, writeStoredLang } from '@/i18n/adminLang'

const LANG_STORAGE_KEY = 'fledw-system-lang'

export default {
  components: { AdminConfirmDialog },
  data: () => ({
    initiated: false,
    systemInformation: [],
    dbDumps: [],
    loading: false,
    dumpLoading: null,
    downloadLoading: null,
    lang: readStoredLang(LANG_STORAGE_KEY),
    tablesMenuProps: {
      offsetY: true,
      bottom: true,
      nudgeBottom: 6,
      maxHeight: '40vh',
      zIndex: 20,
      contentClass: 'sys-filter-menu'
    },
    tables: [
      'account',
      'account_contact',
      'award',
      'changes',
      'conflict',
      'conflict_member_reference',
      'contact',
      'dictionary',
      'lock_keys',
      'managed_dynamic_field',
      'managed_dynamic_label',
      'mapping',
      'mapping_column',
      'messages',
      'period',
      'product',
      'ranking_group',
      'release',
      'sample',
      'score',
      'string_translation',
      'supplier',
      'supplier_award',
      'supplier_identification',
      'supplier_score',
      'taxonomy_term',
      'toplist_label',
      'user'
    ],
    selectedTables: [],
    schemaLoading: null,
    schemaOutput: null,
    schemaOutputKind: null,
    schemaError: null,
    schemaPreviewDone: false,
    schemaApplyConfirm: false
  }),
  computed: {
    dumpList () {
      if (this.dbDumps && typeof this.dbDumps === 'object' && !Array.isArray(this.dbDumps)) {
        return Object.entries(this.dbDumps).map(([file, value]) => {
          if (typeof value === 'object' && value !== null) {
            return {
              file,
              label: value.label || file,
              size: value.size || '',
              createdAt: value.createdAt || ''
            }
          }
          return { file, label: String(value), size: '', createdAt: '' }
        })
      }
      return []
    },
    schemaOutputTitle () {
      if (this.schemaOutputKind === 'preview') {
        return this.t('schemaPreviewTitle')
      }
      if (this.schemaOutputKind === 'validate') {
        return this.t('schemaValidateTitle')
      }
      if (this.schemaOutputKind === 'apply') {
        return this.t('schemaApplyTitle')
      }
      return this.t('schemaOutputTitle')
    },
    schemaOutputDisplay () {
      if (this.schemaLoading === 'preview') {
        return this.t('schemaLoadingPreview')
      }
      if (this.schemaOutput === null) {
        return this.t('schemaNoPreviewYet')
      }
      if (!this.schemaOutput) {
        return this.t('schemaNoChanges')
      }
      return this.schemaOutput
    },
    schemaOutputLooksOk () {
      const text = String(this.schemaOutputDisplay || '')
      return /\[OK\]/i.test(text) || /already in sync/i.test(text) || /bereits synchron/i.test(text)
    }
  },
  methods: {
    t (key) {
      return systemT(this.lang, key)
    },
    setLang (nextLang) {
      if (this.lang === nextLang) {
        return
      }
      this.lang = nextLang
      writeStoredLang(LANG_STORAGE_KEY, nextLang)
    },
    getList: async function () {
      const response = await this.$http.get('/system-information')
      this.systemInformation = response.data
    },
    dbDumpList: async function () {
      const response = await this.$http.get('/db-dump-list')
      this.dbDumps = response.data
    },
    async createFullDump () {
      await this.runDump('full', false, false)
    },
    async createSlimDump () {
      await this.runDump('slim', true, false)
    },
    async createCustomDump () {
      await this.runDump('custom', false, true)
    },
    async runDump (mode, slim, includeTables) {
      this.dumpLoading = mode
      try {
        let url = '/db-dump?slim=' + slim
        if (includeTables && this.selectedTables.length > 0) {
          url += '&tables=' + String(this.selectedTables)
        }
        await this.$http.get(url)
        await this.dbDumpList()
      } finally {
        this.dumpLoading = null
      }
    },
    dbDumpDownload: async function (file) {
      this.downloadLoading = file
      try {
        const response = await this.$http.get(
          '/db-dump-download/' + file,
          { responseType: 'blob' }
        )
        const fileURL = window.URL.createObjectURL(new Blob([response.data]))
        const fileLink = document.createElement('a')
        const fileName = response.headers['content-disposition'].split('filename=').pop()
        fileLink.href = fileURL
        fileLink.setAttribute('download', fileName)
        document.body.appendChild(fileLink)
        fileLink.click()
        document.body.removeChild(fileLink)
      } finally {
        this.downloadLoading = null
      }
    },
    normalizeSchemaOutput (data) {
      if (data == null) {
        return ''
      }
      if (typeof data === 'string') {
        return data.trim()
      }
      if (typeof data === 'object') {
        if (typeof data.detail === 'string') {
          return data.detail
        }
        return JSON.stringify(data, null, 2)
      }
      return String(data)
    },
    formatSchemaError (e) {
      const data = e && e.response ? e.response.data : null
      if (typeof data === 'string' && data.trim()) {
        return data.trim()
      }
      if (data && typeof data === 'object') {
        if (data.detail) {
          return String(data.detail)
        }
        if (data.title) {
          return String(data.title)
        }
        return JSON.stringify(data)
      }
      return (e && e.message) || this.t('schemaRequestFailed')
    },
    async schemaPreview () {
      this.schemaLoading = 'preview'
      this.schemaError = null
      try {
        const response = await this.$http.get('/schema/update', {
          params: {
            'dump-sql': 'true',
            complete: 'true'
          }
        })
        this.schemaOutput = this.normalizeSchemaOutput(response.data)
        this.schemaOutputKind = 'preview'
        this.schemaPreviewDone = true
      } catch (e) {
        this.schemaError = this.formatSchemaError(e)
        this.schemaPreviewDone = false
        this.schemaOutput = null
        this.schemaOutputKind = null
      } finally {
        this.schemaLoading = null
      }
    },
    async schemaValidate () {
      this.schemaLoading = 'validate'
      this.schemaError = null
      try {
        const response = await this.$http.get('/schema/validate')
        this.schemaOutput = this.normalizeSchemaOutput(response.data)
        this.schemaOutputKind = 'validate'
      } catch (e) {
        this.schemaError = this.formatSchemaError(e)
      } finally {
        this.schemaLoading = null
      }
    },
    async schemaApply () {
      this.schemaLoading = 'apply'
      this.schemaError = null
      try {
        const response = await this.$http.get('/schema/update', {
          params: {
            force: 'true',
            complete: 'true'
          }
        })
        this.schemaOutput = this.normalizeSchemaOutput(response.data)
        this.schemaOutputKind = 'apply'
        this.schemaPreviewDone = false
        this.schemaApplyConfirm = false
      } catch (e) {
        this.schemaError = this.formatSchemaError(e)
      } finally {
        this.schemaLoading = null
      }
    }
  },
  async created () {
    await this.$auth.waitForAuth()
    if (!this.$auth.isAuthenticated) {
      await this.$router.push('/')
      return
    }

    this.loading = true
    try {
      await this.getList()
      await this.dbDumpList()
    } finally {
      this.loading = false
      this.initiated = true
    }

    if (this.$auth.isAdmin()) {
      await this.schemaPreview()
    }
  }
}
</script>
